import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import Select from '@ep/components/Select';
import { Body, Body2, H1 } from '@ep/components/Typography';
import Arrow from '@ep/images/arrow.svg';
import CCLogo from '@ep/images/centralCastingLogo.jpg';
import EPLogo from '@ep/images/EP_logo_blue.png';
import VersionedDocumentAsideModule from '@ep/modules/VersionedDocumentAsideModule';
import theme from '@ep/theme';
import { Gcms_Page } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import scrollToJustAbove from '@ep/utils/scrollToJustAbove';
import { MenuItem } from '@mui/material';
import {
  Document,
  Image,
  Page,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import Html from 'react-pdf-html';

export interface VersionedDocumentModuleProps {
  page: Gcms_Page;
}

const StyledContainer = styled(Container)`
  padding: 2.75rem 0 7.5rem;

  ${theme.mediaQueries.tablet} {
    padding: 2.75rem 0 6.25rem;
  }

  ${theme.mediaQueries.mobile} {
    padding: 2.75rem 0;
  }
`;

const Content = styled.div`
  display: flex;
  margin-top: 5rem;
  strong {
    font-family: ${theme.typography.boldFont};
    font-weight: bold;
  }

  ${theme.mediaQueries.mobileAndTablet} {
    flex-direction: column;
    margin-top: 1rem;
  }
`;

const Main = styled(Body)`
  flex: 2;
  p {
    margin-bottom: 0.5rem;
    :empty {
      height: 1rem;
    }
  }
  a:link,
  a:hover,
  a:visited {
    color: ${theme.colors.epLinkBlue};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${theme.colors.duskBlue};
  }

  h2 {
    margin-bottom: 1.2rem;
    padding-top: 2rem;
  }

  h6 {
    font-size: 1.5rem;
  }

  ul {
    padding: 0;
    margin: 0;
    li {
      list-style: none;
      padding-left: 2rem;
      position: relative;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
  }

  ol {
    padding: 0;
    margin: 2rem;
    li {
      list-style: none;
      padding-left: 2rem;
      position: relative;
    }
  }

  li {
    margin-bottom: 0.5rem;
  }

  table,
  th,
  td {
    border: 1px solid;
  }

  table {
    th {
      color: ${theme.colors.white};
      background-color: ${theme.colors.duskBlue};
      border: 1px solid ${theme.colors.black};
    }
    border-collapse: collapse;
    width: 100%;
    td {
      padding: 0.5rem;
      width: 20rem;
      img {
        display: block;
        max-width: 16rem;
        margin: 0 auto;
      }
    }
  }
`;

const RightSideContainer = styled.div`
  flex: 1;

  ${theme.mediaQueries.desktop} {
    margin-left: 2rem;
  }

  ${theme.mediaQueries.mobileAndTablet} {
    margin-top: 2rem;
  }
`;

export const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const BackToTop = styled.div`
  display: flex;
  align-items: center;
  position: fixed;
  bottom: 1.5rem;
  right: 15rem;
  padding: 0 0.5rem;
  font-weight: bold;
  z-index: 1;
  background-color: ${theme.colors.white};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    font-size: small;
    right: 50%;
    transform: translate(50%, 50%);
  }
`;

export const StyledArrow = styled(Arrow)`
  transform: rotate(-90deg);
  margin-right: 0.5rem;
`;

export const MenuLink = styled.a`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const DocumentHeaderContainer = styled(Container)`
  margin-top: 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${theme.mediaQueries.mobileAndTablet} {
    flex-direction: column;
  }
`;

export const SelectContainer = styled('div')`
  width: 19rem;
  svg {
    max-width: 1rem;
  }
  ul {
    padding: 0rem;
    li {
      line-height: 1.3rem;
      font-family: ${theme.typography.primaryFont.regular};
    }
    padding-top: 0;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    margin: 2rem 0;
  }
`;

function VersionedDocumentModule({ page }: VersionedDocumentModuleProps) {
  const [version, setVersion] = useState(0);
  const [scrollElement, setScrollElement] = useState(null);
  const handleChange = (event) => {
    setVersion(event.target.value);
  };
  const menuData = page.legalDocument;
  let readableDate = '';

  const currentDate = new Date();
  const dd = String(currentDate.getDate()).padStart(2, '0');
  const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
  const yyyy = currentDate.getFullYear();

  if (page.legalDocument[0].uploadDate) {
    var myDate = page.legalDocument[version].uploadDate;
    myDate = myDate.split('-');
    var newDate = new Date(myDate[0], myDate[1] - 1, myDate[2]);
    var date = new Date(newDate.getTime());
    let options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    readableDate = Intl.DateTimeFormat('en-US', options).format(date);
  }

  const styles = StyleSheet.create({
    body: {
      padding: '20 40 40',
    },
    image: {
      width: 100,
      marginBottom: 6,
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      borderBottomColor: theme.colors.gray,
      borderBottomStyle: 'solid',
      borderBottomWidth: 1,
      marginBottom: 8,
    },
    headerTitle: {
      fontSize: 6,
      color: theme.colors.darkGray,
    },
    date: {
      fontSize: 6,
      color: theme.colors.darkGray,
      marginBottom: 8,
    },
    title: {
      fontSize: 14,
      color: theme.colors.duskBlue,
      marginBottom: 8,
    },
    doc: {
      fontSize: 10,
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 6,
      bottom: 20,
      right: 40,
      textAlign: 'left',
      color: theme.colors.darkGray,
    },
  });

  const tableStyle = {
    table: {
      borderCollapse: 'collapse',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.colors.black,
    },
    th: {
      color: theme.colors.white,
      backgroundColor: theme.colors.duskBlue,
      textAlign: 'center',
      padding: '0 10px',
    },
    li: {
      width: '80%',
      margin: '4px',
    },
    td: {
      backgroundColor: theme.colors.white,
      padding: '0 10px',
    },
  };

  const LogoSwitcher = () => {
    switch (page.pdfLogo) {
      case 'entertainment_partners':
        return EPLogo;
      case 'central_casting':
        return CCLogo;
      default:
        return EPLogo;
    }
  };

  const LegalPDF = () => (
    <Document>
      <Page style={styles.body}>
        <View style={styles.header} fixed>
          <View>
            {page.legalDocument[version].title && (
              <Text style={styles.headerTitle}>
                {page.legalDocument[version].title}
              </Text>
            )}
            {readableDate && (
              <Text style={styles.date}>{`Effective ${readableDate}`}</Text>
            )}
          </View>
          <View>
            <Image style={styles.image} src={LogoSwitcher()} />
          </View>
        </View>
        <View>
          {page.legalDocument[version].title && (
            <Text style={styles.title}>
              {page.legalDocument[version].title}
            </Text>
          )}
          <Html stylesheet={tableStyle} style={styles.doc}>
            {page.legalDocument[version].body.html}
          </Html>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}\nDownloaded on ${mm}/${dd}/${yyyy}`
          }
          fixed
        />
      </Page>
    </Document>
  );

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  let displayDate = DateTime.fromISO(menuData[version].uploadDate);

  var fileDate = '';
  if (page.legalDocument[version].uploadDate) {
    var rawDate = page.legalDocument[version].uploadDate.split('-');
    fileDate = `${rawDate[1]}-${rawDate[2]}-${rawDate[0]}`;
  }

  useEffect(() => {
    if (document.getElementsByClassName(`section`)) {
      setScrollElement(document.getElementsByClassName(`section`));
    }
  }, []);

  return (
    <>
      <DocumentHeaderContainer>
        <SelectContainer>
          {page.legalDocument[0].uploadDate && (
            <Select
              onChange={handleChange}
              style={{
                border: '0px solid white',
                backgroundColor: 'white',
                marginLeft: '-1rem',
                width: '20rem',
              }}
              value={version}
            >
              {menuData.map(
                (item, index: number) => (
                  (displayDate = DateTime.fromISO(item.uploadDate)),
                  (
                    <MenuItem value={index}>
                      {'Effective ' +
                        displayDate.toLocaleString(DateTime.DATE_FULL)}
                    </MenuItem>
                  )
                ),
              )}
            </Select>
          )}
        </SelectContainer>
        {isClient && (
          <PDFDownloadLink
            document={<LegalPDF />}
            fileName={`${page.title.replaceAll(' ', '-')}-${fileDate}.pdf`}
          >
            {({ blob, url, loading, error }) =>
              loading ? 'Loading Document' : 'Download PDF'
            }
          </PDFDownloadLink>
        )}
      </DocumentHeaderContainer>

      <StyledContainer>
        {page.legalDocument[version].title && (
          <H1>{page.legalDocument[version].title}</H1>
        )}
        {page.legalDocument[version].sectionNames.length > 0 && (
          <MenuContainer>
            {page.legalDocument[version].sectionNames.map(
              (title: string, index: number) => {
                return (
                  <MenuLink
                    onClick={() => scrollToJustAbove(scrollElement[index], 100)}
                  >
                    {title}
                  </MenuLink>
                );
              },
            )}
          </MenuContainer>
        )}
        <Content>
          <Main {...getHtmlProp(page.legalDocument[version].body.html)} />
          {page.sidebar && (
            <RightSideContainer {...getHtmlProp(page.sidebar.html)} />  
          )}
          {page.legalDocument[version].sectionNames.length > 0 && (
            <BackToTop onClick={() => scrollTo({ top: 0, behavior: 'smooth' })}>
              <StyledArrow />
              <p>Back to top</p>
            </BackToTop>
          )}
        </Content>
      </StyledContainer>
    </>
  );
}

export default VersionedDocumentModule;
